import { useState } from "react";
import { Send, MessageCircle, Check, ArrowRight } from "lucide-react";

const WA_LINK = "https://wa.me/905316224378";

// variant: "light" | "dark"; service: "yacht" | "wedding"
export default function HubLeadForm({ lang, variant = "light", service = "wedding", cities = [], copy }) {
  const dark = variant === "dark";
  const [form, setForm] = useState({ name: "", contact: "", wedding_date: "", city: cities[0]?.slug || "", message: "" });
  const [state, setState] = useState("idle");
  const [err, setErr] = useState("");
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name.trim() || form.name.trim().length < 2 || !form.contact.trim() || form.contact.trim().length < 4) {
      setErr(copy.required);
      return;
    }
    setState("sending");
    try {
      const r = await fetch(`/api/leads/wedding`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          service,
          lang,
          source_page: typeof window !== "undefined" ? window.location.pathname : "",
        }),
      });
      if (!r.ok) throw new Error("bad");
      setState("success");
    } catch (_e) {
      setState("error");
    }
  };

  const label = dark ? "text-white/60" : "text-[#6B5D4F]";
  const inputCls = dark
    ? "w-full bg-white/5 border border-white/15 rounded-sm px-4 py-3 text-sm text-white placeholder-white/30 focus:border-[#F5C77A] focus:outline-none transition"
    : "w-full bg-white border border-[#F3DDE1] rounded-sm px-4 py-3 text-sm text-[#1F1815] placeholder-[#C9A6AC] focus:border-[#C25E72] focus:outline-none transition";
  const accent = dark ? "text-[#F5C77A]" : "text-[#C25E72]";
  const btnCls = dark
    ? "bg-gradient-to-r from-[#FFE5A8] via-[#F5C77A] to-[#E0A954] hover:from-[#FFEDB8] hover:to-[#E8B86C] text-[#0A0807]"
    : "bg-gradient-to-r from-[#F2B8C4] via-[#E39FAC] to-[#C25E72] hover:from-[#F7C6CF] hover:to-[#D98096] text-[#1F1815]";

  return (
    <section
      id="request"
      className={dark ? "relative bg-[#0A0807] py-16 sm:py-24 lg:py-32 overflow-hidden" : "relative bg-white py-16 sm:py-24 lg:py-32 overflow-hidden border-t border-[#F3DDE1]"}
      data-testid="hub-leadform"
    >
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
           style={{ backgroundImage: `radial-gradient(${dark ? "#D4B896" : "#F3DDE1"} 0.5px, transparent 0.5px)`, backgroundSize: "26px 26px" }} />
      {!dark && (
        <>
          <div className="absolute -top-24 right-[-8%] w-[440px] h-[440px] rounded-full bg-[#F6C9CF]/35 blur-3xl pointer-events-none" />
          <div className="absolute bottom-[-15%] left-[-8%] w-[400px] h-[400px] rounded-full bg-[#E39FAC]/18 blur-3xl pointer-events-none" />
        </>
      )}

      <div className="relative max-w-3xl mx-auto px-5 sm:px-10">
        <div className="text-center mb-10 sm:mb-12">
          <p className={`text-[10px] sm:text-[11px] tracking-[0.3em] sm:tracking-[0.35em] ${accent} mb-3 sm:mb-4 font-medium`}>{copy.eyebrow}</p>
          <h2 className={`text-3xl sm:text-4xl lg:text-5xl leading-tight ${dark ? "text-white" : "text-[#1F1815]"}`}
              style={{ fontFamily: dark ? "'Cormorant Garamond', serif" : "'Playfair Display', serif", fontWeight: 400 }}>
            {copy.title} <span className={`italic ${accent}`}>{copy.titleAccent}</span>
          </h2>
          <p className={`text-sm sm:text-base font-light mt-4 sm:mt-5 max-w-xl mx-auto leading-relaxed ${dark ? "text-white/65" : "text-[#6B5D4F]"}`}>{copy.subtitle}</p>
        </div>

        {state === "success" ? (
          <div className={`text-center py-12 sm:py-16 rounded-sm ${dark ? "bg-white/5 border border-white/10" : "bg-[#FFF6F8] border border-[#F3DDE1]"}`} data-testid="hub-leadform-success">
            <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5 ${dark ? "bg-[#F5C77A]" : "bg-[#C25E72]"}`}>
              <Check className={`w-7 h-7 ${dark ? "text-[#0A0807]" : "text-white"}`} />
            </div>
            <h3 className={`text-2xl sm:text-3xl mb-3 ${dark ? "text-white" : "text-[#1F1815]"}`} style={{ fontFamily: "'Cormorant Garamond', serif" }}>{copy.successTitle}</h3>
            <p className={`text-sm font-light max-w-md mx-auto ${dark ? "text-white/60" : "text-[#6B5D4F]"}`}>{copy.successMsg}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 sm:space-y-5" data-testid="hub-leadform-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              <div>
                <label className={`block text-[11px] tracking-[0.15em] uppercase mb-2 ${label}`}>{copy.nameLabel} *</label>
                <input type="text" value={form.name} onChange={update("name")} className={inputCls} placeholder={copy.namePh} data-testid="hub-leadform-name" />
              </div>
              <div>
                <label className={`block text-[11px] tracking-[0.15em] uppercase mb-2 ${label}`}>{copy.contactLabel} *</label>
                <input type="text" value={form.contact} onChange={update("contact")} className={inputCls} placeholder={copy.contactPh} data-testid="hub-leadform-contact" />
              </div>
              <div>
                <label className={`block text-[11px] tracking-[0.15em] uppercase mb-2 ${label}`}>{copy.dateLabel}</label>
                <input type="text" value={form.wedding_date} onChange={update("wedding_date")} className={inputCls} placeholder={copy.datePh} data-testid="hub-leadform-date" />
              </div>
              <div>
                <label className={`block text-[11px] tracking-[0.15em] uppercase mb-2 ${label}`}>{copy.cityLabel}</label>
                <select value={form.city} onChange={update("city")} className={inputCls} data-testid="hub-leadform-city">
                  {cities.map((c) => <option key={c.slug} value={c.slug} className="text-[#1F1815]">{c.label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className={`block text-[11px] tracking-[0.15em] uppercase mb-2 ${label}`}>{copy.messageLabel}</label>
              <textarea value={form.message} onChange={update("message")} rows={4} className={inputCls} placeholder={copy.messagePh} data-testid="hub-leadform-message" />
            </div>

            {err && <p className="text-[#E06A5A] text-sm" data-testid="hub-leadform-error">{err}</p>}
            {state === "error" && <p className="text-[#E06A5A] text-sm">{copy.errorMsg}</p>}

            <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 pt-2">
              <button type="submit" disabled={state === "sending"}
                      className={`group w-full sm:w-auto inline-flex items-center justify-center gap-3 px-9 py-4 text-[12px] tracking-[0.25em] font-semibold transition-all duration-500 rounded-sm disabled:opacity-60 ${btnCls}`}
                      data-testid="hub-leadform-submit">
                <Send className="w-4 h-4" />
                <span>{state === "sending" ? copy.sending : copy.submit}</span>
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
              <a href={WA_LINK} target="_blank" rel="noopener noreferrer"
                 className={`w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-4 text-[12px] tracking-[0.2em] font-medium rounded-sm border transition ${dark ? "border-white/40 text-white hover:bg-white/10" : "border-[#1F1815]/30 text-[#1F1815] hover:bg-[#1F1815]/5"}`}
                 data-testid="hub-leadform-whatsapp">
                <MessageCircle className="w-4 h-4" />
                <span>{copy.orWhatsApp}</span>
              </a>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
